import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";
import {
  getCollectionDocuments,
  updateCollectionDocument,
  deleteCollectionDocument,
} from "../api/admin";
import { uploadUrl, upload } from "../api/uploadConfig.js";
import toast from "react-hot-toast";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Database,
  Edit3,
  FileText,
  ImageOff,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";

const HIDDEN_FIELDS = new Set([
  "password",
  "__v",
  "_id",
  "emailVerificationOTP",
  "loginOTP",
  "embedding",
  "googleId",
  "facebookId",
]);

const OBJECT_ID_REGEX = /^[a-f\d]{24}$/i;

const isRawObjectId = (value) =>
  typeof value === "string" && OBJECT_ID_REGEX.test(value);

const resolveImageUrl = (raw) => {
  if (!raw) return null;
  let value = raw;
  if (Array.isArray(value)) value = value[0];
  if (!value || typeof value !== "string") return null;
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  if (value.startsWith("/uploads")) return `${upload}${value}`;
  if (value.startsWith("uploads/")) return `${upload}/${value}`;
  if (value.startsWith("/")) return `${upload}${value}`;
  return `${uploadUrl}/${value}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? `${value.length} items` : "-";
  if (typeof value === "object") {
    if (value.$oid) return "-";
    return value.name || value.title || value.email || "-";
  }
  if (isRawObjectId(value)) return "-";
  return String(value);
};

// Render a reference (ObjectId) field as human-readable text.
// Populated documents arrive as objects; unresolved raw ObjectIds are hidden.
const getReferenceDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return isRawObjectId(value) ? "-" : value;
  if (typeof value === "object") {
    if (value.name) return value.name;
    if (value.title) return value.title;
    if (value.email) return value.email;
    return "-";
  }
  return "-";
};

// Prefer a populated reference; otherwise fall back to a plain string field.
const refOrFallback = (refValue, fallback) => {
  const resolved = getReferenceDisplay(refValue);
  if (resolved && resolved !== "-") return resolved;
  if (fallback && !isRawObjectId(fallback)) return fallback;
  return "-";
};

const POSITIVE_STATUS = new Set([
  "published",
  "active",
  "live",
  "resolved",
  "verified",
  "approved",
  "yes",
  "processed",
  "clustered",
]);
const WARNING_STATUS = new Set([
  "pending",
  "draft",
  "in progress",
  "processing",
  "merged",
]);
const NEGATIVE_STATUS = new Set([
  "ended",
  "archived",
  "rejected",
  "bug",
  "failed",
  "no",
  "inactive",
]);

const getBadgeTone = (value) => {
  const key = String(value ?? "").trim().toLowerCase();
  if (POSITIVE_STATUS.has(key)) return "green";
  if (WARNING_STATUS.has(key)) return "amber";
  if (NEGATIVE_STATUS.has(key)) return "red";
  return "blue";
};

const badgeClass = (tone) => {
  const base =
    "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap";
  switch (tone) {
    case "green":
      return `${base} bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300`;
    case "amber":
      return `${base} bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300`;
    case "red":
      return `${base} bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300`;
    default:
      return `${base} bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300`;
  }
};

const truncate = (value, max) =>
  typeof value === "string" && value.length > max
    ? `${value.slice(0, max)}...`
    : value;

/**
 * Per-collection column definitions.
 * Column options:
 *   key        - document field
 *   label      - header text
 *   type       - "date" | "image" | "boolean" | "badge" | "reference" | "number"
 *   wide       - allow wider cell
 *   truncate   - max characters for long text
 *   resolver   - custom (doc) => value
 *   sortKey    - override sort field (defaults to key; resolver/image => not sortable)
 *   sortable   - explicitly enable/disable sorting
 */
const COLLECTION_COLUMNS = {
  users: [
    { key: "photo", label: "Photo", type: "image" },
    { key: "name", label: "Name", wide: true },
    { key: "email", label: "Email" },
    { key: "isEmailVerified", label: "Verified", type: "boolean" },
    { key: "isAdmin", label: "Admin", type: "boolean" },
    { key: "createdAt", label: "Joined", type: "date" },
  ],
  news: [
    { key: "media", label: "Image", type: "image" },
    { key: "title", label: "Title", wide: true, truncate: 80 },
    { key: "category", label: "Category", type: "badge" },
    {
      key: "author",
      label: "Author",
      resolver: (doc) => refOrFallback(doc.authorId, doc.author),
    },
    { key: "language", label: "Language" },
    { key: "publishedAt", label: "Published", type: "date" },
  ],
  contributednews: [
    { key: "media", label: "Image", type: "image" },
    { key: "title", label: "Title", wide: true, truncate: 80 },
    { key: "category", label: "Category", type: "badge" },
    { key: "author", label: "Author" },
    { key: "language", label: "Language" },
    { key: "views", label: "Views", type: "number" },
    { key: "submittedAt", label: "Submitted", type: "date" },
  ],
  localnews: [
    { key: "image", label: "Image", type: "image" },
    { key: "title", label: "Title", wide: true, truncate: 90 },
    { key: "source", label: "Source" },
    { key: "time", label: "Time" },
    { key: "createdAt", label: "Created", type: "date" },
  ],
  localnewssources: [
    { key: "name", label: "Name", wide: true },
    { key: "url", label: "URL", truncate: 40 },
    { key: "country", label: "Country" },
    { key: "state", label: "State" },
    { key: "city", label: "City" },
    { key: "language", label: "Language" },
    { key: "isActive", label: "Active", type: "boolean" },
    { key: "priority", label: "Priority", type: "number" },
  ],
  aggregatednews: [
    { key: "imageUrl", label: "Image", type: "image" },
    { key: "title", label: "Title", wide: true, truncate: 80 },
    {
      key: "source",
      label: "Source",
      resolver: (doc) => doc.source?.name || "-",
      sortKey: "source.name",
    },
    { key: "category", label: "Category", type: "badge" },
    { key: "language", label: "Language" },
    { key: "status", label: "Status", type: "badge" },
    { key: "publishedAt", label: "Published", type: "date" },
  ],
  newsclusters: [
    { key: "title", label: "Title", wide: true, truncate: 80 },
    { key: "category", label: "Category", type: "badge" },
    { key: "language", label: "Language" },
    { key: "articleCount", label: "Articles", type: "number" },
    {
      key: "representativeArticle",
      label: "Top Article",
      resolver: (doc) => getReferenceDisplay(doc.representativeArticle),
    },
    { key: "status", label: "Status", type: "badge" },
    { key: "lastPublished", label: "Last Published", type: "date" },
  ],
  feedbacks: [
    { key: "type", label: "Type", type: "badge" },
    { key: "message", label: "Message", wide: true, truncate: 90 },
    { key: "rating", label: "Rating", type: "number" },
    {
      key: "user",
      label: "User",
      resolver: (doc) =>
        refOrFallback(doc.userId, doc.contactName || doc.contactEmail),
    },
    { key: "status", label: "Status", type: "badge" },
    { key: "createdAt", label: "Date", type: "date" },
  ],
  freelancers: [
    { key: "image", label: "Image", type: "image" },
    { key: "name", label: "Name", wide: true },
    { key: "description", label: "Description", wide: true, truncate: 90 },
    { key: "time", label: "Time" },
    { key: "createdAt", label: "Created", type: "date" },
  ],
  livesessions: [
    { key: "title", label: "Title", wide: true, truncate: 70 },
    {
      key: "host",
      label: "Host",
      resolver: (doc) => getReferenceDisplay(doc.host),
    },
    {
      key: "guest",
      label: "Guest",
      resolver: (doc) => getReferenceDisplay(doc.guest),
    },
    { key: "status", label: "Status", type: "badge" },
    { key: "startedAt", label: "Started", type: "date" },
    { key: "endedAt", label: "Ended", type: "date" },
  ],
  videopodcastchannels: [
    { key: "thumbnail", label: "Thumbnail", type: "image" },
    { key: "title", label: "Title", wide: true, truncate: 80 },
    {
      key: "author",
      label: "Author",
      resolver: (doc) => refOrFallback(doc.authorId, doc.author),
    },
    { key: "category", label: "Category", type: "badge" },
    { key: "language", label: "Language" },
    { key: "views", label: "Views", type: "number" },
    { key: "isPublished", label: "Published", type: "boolean" },
    { key: "createdAt", label: "Created", type: "date" },
  ],
  videopodcastcomments: [
    {
      key: "podcast",
      label: "Podcast",
      wide: true,
      resolver: (doc) => getReferenceDisplay(doc.podcastId),
    },
    {
      key: "user",
      label: "User",
      resolver: (doc) => getReferenceDisplay(doc.userId),
    },
    {
      key: "email",
      label: "Email",
      resolver: (doc) =>
        doc.userId && typeof doc.userId === "object" ? doc.userId.email || "-" : "-",
    },
    { key: "text", label: "Comment", wide: true, truncate: 100 },
    { key: "createdAt", label: "Created", type: "date" },
  ],
  topstories: [
    { key: "image", label: "Image", type: "image" },
    { key: "title", label: "Title", wide: true, truncate: 90 },
    { key: "description", label: "Description", wide: true, truncate: 90 },
    { key: "publishedAt", label: "Published", type: "date" },
  ],
  notifications: [
    { key: "type", label: "Type", type: "badge" },
    { key: "message", label: "Message", wide: true, truncate: 90 },
    {
      key: "user",
      label: "User",
      resolver: (doc) => getReferenceDisplay(doc.userId),
    },
    {
      key: "actor",
      label: "Actor",
      resolver: (doc) => getReferenceDisplay(doc.actor),
    },
    { key: "read", label: "Read", type: "boolean" },
    { key: "createdAt", label: "Date", type: "date" },
  ],
  authors: [
    { key: "name", label: "Name", wide: true },
    { key: "email", label: "Email" },
    { key: "bio", label: "Bio", wide: true, truncate: 90 },
  ],
  saved_searches: [
    { key: "keyword", label: "Keyword", wide: true },
    {
      key: "user",
      label: "User",
      resolver: (doc) => getReferenceDisplay(doc.userId),
    },
    { key: "created_at", label: "Created", type: "date" },
  ],
  userpreferences: [
    {
      key: "user",
      label: "User",
      resolver: (doc) => getReferenceDisplay(doc.userId),
    },
    { key: "personalizationEnabled", label: "Personalized", type: "boolean" },
    { key: "totalArticlesRead", label: "Articles Read", type: "number" },
    { key: "lastActiveAt", label: "Last Active", type: "date" },
  ],
};

const isDisplayableField = (key, value) => {
  if (HIDDEN_FIELDS.has(key)) return false;
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return false;
  if (typeof value === "object") return Boolean(value.name || value.email || value.title);
  if (isRawObjectId(value)) return false;
  return true;
};

// Auto-detect columns for collections without an explicit definition.
const buildAutoColumns = (documents) => {
  if (!documents.length) {
    return [
      { key: "title", label: "Title", wide: true },
      { key: "name", label: "Name", wide: true },
      { key: "status", label: "Status", type: "badge" },
    ];
  }

  const keys = new Set();
  documents.forEach((doc) => {
    Object.entries(doc).forEach(([key, value]) => {
      if (isDisplayableField(key, value)) keys.add(key);
    });
  });

  const preferred = [
    "title",
    "name",
    "source",
    "text",
    "message",
    "description",
    "category",
    "author",
    "email",
    "type",
    "status",
    "rating",
    "url",
    "createdAt",
    "updatedAt",
  ];

  const selectedKeys = [
    ...preferred.filter((key) => keys.has(key)),
    ...[...keys].filter((key) => !preferred.includes(key)),
  ].slice(0, 6);

  return selectedKeys.map((key) => ({
    key,
    label: key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (char) => char.toUpperCase()),
    wide: ["title", "name", "text", "message", "description", "url"].includes(key),
    type:
      key.toLowerCase().includes("date") || /At$/.test(key)
        ? "date"
        : ["image", "thumbnail", "photo", "imageurl"].includes(key.toLowerCase())
        ? "image"
        : key === "category" || key === "type" || key === "status"
        ? "badge"
        : undefined,
    truncate: ["message", "description", "url", "text"].includes(key) ? 90 : undefined,
  }));
};

const getColumnsFor = (collectionName, documents) =>
  COLLECTION_COLUMNS[collectionName.toLowerCase()] || buildAutoColumns(documents);

const isSortable = (column) => {
  if (column.sortable === false) return false;
  if (column.sortable === true) return true;
  if (column.sortKey) return true;
  return !column.resolver && column.type !== "image";
};

const getCellText = (doc, column) => {
  if (column.resolver) return column.resolver(doc) ?? "-";
  const value = doc[column.key];
  if (column.type === "date") return formatDate(value);
  if (column.type === "number")
    return value === null || value === undefined ? "-" : String(value);
  if (typeof value === "object") return getReferenceDisplay(value);
  const formatted = formatValue(value);
  return column.truncate ? truncate(formatted, column.truncate) : formatted;
};

const getEditableDocument = (doc) => {
  const copy = { ...doc };
  ["password", "__v"].forEach((field) => delete copy[field]);
  delete copy._id;
  return copy;
};

const CollectionDetail = ({ collectionName }) => {
  const { adminToken } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [editDoc, setEditDoc] = useState(null);
  const [editJson, setEditJson] = useState("");
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const limit = 20;

  const fetchDocuments = useCallback(async () => {
    if (!adminToken || !collectionName) return;

    try {
      setLoading(true);
      const response = await getCollectionDocuments(collectionName, adminToken, page, limit, {
        search: searchTerm,
        sortBy,
        sortOrder,
      });
      if (response.success) {
        setDocuments(response.documents || []);
        setTotalPages(response.totalPages || 1);
        setTotal(response.total || 0);
      } else {
        toast.error("Failed to load collection documents");
      }
    } catch (error) {
      console.error("Error fetching collection documents:", error);
      toast.error(error.response?.data?.message || "Failed to load collection documents");
    } finally {
      setLoading(false);
    }
  }, [adminToken, collectionName, page, searchTerm, sortBy, sortOrder]);

  // Reset view state when switching collections.
  useEffect(() => {
    setPage(1);
    setSearchInput("");
    setSearchTerm("");
    setSortBy("");
    setSortOrder("desc");
  }, [collectionName]);

  // Debounce the search input into the applied search term.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm((current) => {
        if (current !== searchInput) setPage(1);
        return searchInput;
      });
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const columns = useMemo(
    () => getColumnsFor(collectionName, documents),
    [collectionName, documents]
  );

  const handleSort = (column) => {
    if (!isSortable(column)) return;
    const field = column.sortKey || column.key;
    if (sortBy === field) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const openEditModal = (doc) => {
    setEditDoc(doc);
    setEditJson(JSON.stringify(getEditableDocument(doc), null, 2));
  };

  const handleSaveEdit = async () => {
    if (!editDoc || !adminToken) return;

    try {
      setSaving(true);
      const parsed = JSON.parse(editJson);
      const response = await updateCollectionDocument(
        collectionName,
        editDoc._id,
        parsed,
        adminToken
      );

      if (response.success) {
        toast.success("Document updated successfully");
        setEditDoc(null);
        fetchDocuments();
      } else {
        toast.error(response.message || "Failed to update document");
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON format");
      } else {
        toast.error(error.response?.data?.message || "Failed to update document");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDoc || !adminToken) return;

    try {
      setDeleting(true);
      const response = await deleteCollectionDocument(
        collectionName,
        deleteDoc._id,
        adminToken
      );

      if (response.success) {
        toast.success("Document deleted successfully");
        setDeleteDoc(null);
        fetchDocuments();
      } else {
        toast.error(response.message || "Failed to delete document");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete document");
    } finally {
      setDeleting(false);
    }
  };

  const renderCell = (doc, column) => {
    if (column.type === "image") {
      const src = resolveImageUrl(column.resolver ? column.resolver(doc) : doc[column.key]);
      if (!src) {
        return (
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-400 dark:bg-gray-700/60">
            <ImageOff className="h-4 w-4" />
          </span>
        );
      }
      return (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="h-10 w-10 rounded-lg object-cover ring-1 ring-black/5"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      );
    }

    if (column.type === "boolean") {
      const value = column.resolver ? column.resolver(doc) : doc[column.key];
      return (
        <span className={badgeClass(value ? "green" : "red")}>{value ? "Yes" : "No"}</span>
      );
    }

    const text = getCellText(doc, column);

    if (column.type === "badge") {
      if (text === "-") return <span className="text-gray-400">-</span>;
      return <span className={badgeClass(getBadgeTone(text))}>{text}</span>;
    }

    return text;
  };

  return (
    <div className="w-full overflow-x-hidden">
      <div className="mb-6 rounded-3xl border border-white/20 bg-gradient-to-r from-red-500/10 via-white/70 to-red-500/10 p-5 shadow-xl shadow-red-500/10 backdrop-blur-xl dark:border-gray-700/30 dark:from-red-900/20 dark:via-gray-800/70 dark:to-red-900/20 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/admin/databases")}
              className="rounded-2xl border border-white/30 bg-white/70 p-3 shadow-lg transition hover:scale-105 hover:bg-white dark:border-gray-600/40 dark:bg-gray-800/70 dark:hover:bg-gray-800"
              aria-label="Back to collections"
            >
              <ArrowLeft className="h-5 w-5 text-red-600 dark:text-red-400" />
            </button>
            <div className="min-w-0">
              <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-red-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
                <Database className="h-3.5 w-3.5" />
                Collection
              </div>
              <h1 className="break-words text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
                {collectionName}
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {total.toLocaleString()} documents total
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={fetchDocuments}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-red-500 to-red-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-red-500/30 transition hover:from-red-600 hover:to-red-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="mb-5 rounded-2xl border border-white/20 bg-white/60 p-4 shadow-lg backdrop-blur-xl dark:border-gray-700/30 dark:bg-gray-800/60">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={`Search in ${collectionName}...`}
            className="w-full rounded-xl border border-white/20 bg-white/70 py-3 pl-10 pr-10 text-sm text-gray-800 shadow-inner outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-400/30 dark:border-gray-600/30 dark:bg-gray-900/40 dark:text-white"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="rounded-3xl border border-white/20 bg-white/60 py-16 text-center shadow-xl backdrop-blur-xl dark:border-gray-700/30 dark:bg-gray-800/60">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-red-500 border-t-transparent" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading documents...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="rounded-3xl border border-white/20 bg-white/60 py-16 text-center shadow-xl backdrop-blur-xl dark:border-gray-700/30 dark:bg-gray-800/60">
          <FileText className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <p className="text-gray-600 dark:text-gray-300">
            {searchTerm ? "No documents match your search" : "No documents in this collection"}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-3xl border border-white/20 bg-white/70 shadow-2xl shadow-black/5 backdrop-blur-xl dark:border-gray-700/30 dark:bg-gray-800/70">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gradient-to-r from-red-50 to-white dark:from-red-950/30 dark:to-gray-900/40">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    #
                  </th>
                  {columns.map((column) => {
                    const sortable = isSortable(column);
                    const field = column.sortKey || column.key;
                    const active = sortBy === field;
                    return (
                      <th
                        key={column.key}
                        onClick={() => handleSort(column)}
                        className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 ${
                          sortable ? "cursor-pointer select-none hover:text-red-600 dark:hover:text-red-400" : ""
                        }`}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          {column.label}
                          {sortable &&
                            (active ? (
                              sortOrder === "asc" ? (
                                <ArrowUp className="h-3.5 w-3.5 text-red-500" />
                              ) : (
                                <ArrowDown className="h-3.5 w-3.5 text-red-500" />
                              )
                            ) : (
                              <ArrowUpDown className="h-3 w-3 opacity-40" />
                            ))}
                        </span>
                      </th>
                    );
                  })}
                  <th className="px-4 py-4 text-right text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
                {documents.map((doc, index) => (
                  <tr
                    key={doc._id}
                    className="transition hover:bg-red-50/60 dark:hover:bg-red-950/20"
                  >
                    <td className="px-4 py-4 text-sm font-semibold text-gray-500 dark:text-gray-400">
                      {(page - 1) * limit + index + 1}
                    </td>
                    {columns.map((column) => {
                      const cell = renderCell(doc, column);
                      const titleText = typeof cell === "string" ? cell : undefined;
                      return (
                        <td
                          key={`${doc._id}-${column.key}`}
                          className={`px-4 py-4 text-sm text-gray-700 dark:text-gray-200 ${
                            column.type === "image"
                              ? ""
                              : column.wide
                              ? "max-w-[320px] truncate"
                              : "max-w-[180px] truncate"
                          }`}
                          title={titleText}
                        >
                          {cell}
                        </td>
                      );
                    })}
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEditModal(doc)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/60"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteDoc(doc)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 transition hover:bg-red-100 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/60"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(current - 1, 1))}
            disabled={page === 1 || loading}
            className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/70 px-4 py-2 text-sm font-medium disabled:opacity-50 dark:border-gray-600/30 dark:bg-gray-800/70"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <span className="rounded-full bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-700 dark:text-red-300">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
            disabled={page === totalPages || loading}
            className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/70 px-4 py-2 text-sm font-medium disabled:opacity-50 dark:border-gray-600/30 dark:bg-gray-800/70"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {editDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-3xl border border-white/20 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Edit Document</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">ID: {editDoc._id}</p>
              </div>
              <button
                type="button"
                onClick={() => setEditDoc(null)}
                className="rounded-xl p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-6">
              <textarea
                value={editJson}
                onChange={(e) => setEditJson(e.target.value)}
                className="min-h-[320px] w-full rounded-2xl border border-gray-200 bg-gray-50 p-4 font-mono text-sm text-gray-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
              />
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setEditDoc(null)}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium dark:border-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={saving}
                className="rounded-xl bg-gradient-to-r from-red-500 to-red-600 px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl border border-white/20 bg-white p-6 shadow-2xl dark:border-gray-700 dark:bg-gray-900">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 dark:bg-red-950/40">
              <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Delete Document?</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              This will permanently delete the document from <strong>{collectionName}</strong>.
            </p>
            <p className="mt-3 rounded-xl bg-gray-50 p-3 font-mono text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {deleteDoc._id}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteDoc(null)}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium dark:border-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-xl bg-red-600 px-5 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionDetail;
